import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageShell, redButton, goldButton } from '../components/PageShell';
import { useAuth } from '../context/AuthContext';
import { orderApi } from '../lib/orderApi';

const STATUS_LABELS = {
  paid: 'Confirmado', preparing: 'Preparando', out_for_delivery: 'En ruta',
  arriving_soon: 'Llegando', delivered: 'Entregado', cancelled: 'Cancelado', pending_payment: 'Pago pendiente',
};

const inputStyle = { width: '100%', background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)', color: '#F5F5F0', padding: '11px 13px', fontSize: '13px', outline: 'none' };

const MyOrders = () => {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ready, setReady] = useState(false);
  const [phone, setPhone] = useState('');
  const [addr, setAddr] = useState('');
  const [saved, setSaved] = useState(false);
  const [savingBusy, setSavingBusy] = useState(false);
  const [mkt, setMkt] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login?next=/mis-pedidos'); return; }
    setPhone(user.phone || '');
    setAddr(user.saved_address || '');
    setMkt(!!user.marketing_opt_in);
    orderApi.get('/orders/mine').then(({ data }) => setOrders(data.orders)).finally(() => setReady(true));
  }, [user, loading, navigate]);

  const saveProfile = async () => {
    setSavingBusy(true);
    setSaved(false);
    try {
      await orderApi.patch('/auth/profile', { phone, saved_address: addr, marketing_opt_in: mkt });
      if (refreshUser) await refreshUser();
      setSaved(true);
    } finally {
      setSavingBusy(false);
    }
  };

  return (
    <PageShell>
      <h1 className="font-display" style={{ fontSize: '34px', fontWeight: 700 }}>Mi Perfil</h1>

      {user && (
        <div className="mt-6 p-5" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.2)' }} data-testid="profile-card">
          <p className="font-body" style={{ fontSize: '15px', color: '#F5F5F0', fontWeight: 500 }}>{user.name}</p>
          <p className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>{user.email}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Teléfono</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} className="mt-1" placeholder="Teléfono" data-testid="profile-phone" />
            </div>
            <div>
              <label className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Dirección guardada</label>
              <input value={addr} onChange={(e) => setAddr(e.target.value)} style={inputStyle} className="mt-1" placeholder="Dirección de entrega" data-testid="profile-address" />
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <button onClick={saveProfile} disabled={savingBusy} style={{ ...goldButton, opacity: savingBusy ? 0.6 : 1 }} data-testid="profile-save">
              {savingBusy ? 'Guardando…' : 'Guardar'}
            </button>
            {saved && <span className="font-body" style={{ fontSize: '12px', color: '#7FB77E' }} data-testid="profile-saved">✓ Guardado</span>}
          </div>
          <label className="flex items-center gap-2 mt-4 cursor-pointer" data-testid="profile-marketing-label">
            <input type="checkbox" checked={mkt} onChange={(e) => setMkt(e.target.checked)} style={{ accentColor: '#C9A84C', width: '15px', height: '15px' }} data-testid="profile-marketing" />
            <span className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>Recibir ofertas y novedades por email</span>
          </label>
          <p className="font-body mt-3" style={{ fontSize: '11px', color: '#C2C1B6' }}>Tu ubicación también se guarda automáticamente al ordenar.</p>
        </div>
      )}

      <h2 className="font-display mt-10" style={{ fontSize: '24px', fontWeight: 700 }}>Pedidos pasados</h2>
      {ready && orders.length === 0 && (
        <div className="mt-4">
          <p className="font-body" style={{ fontSize: '14px', color: '#C2C1B6' }}>Todavía no tienes pedidos.</p>
          <Link to="/pedir" style={{ ...redButton, display: 'inline-block', marginTop: '16px' }}>Pedir ahora</Link>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {orders.map((o) => (
          <Link key={o.id} to={`/pedido/${o.id}`} className="block p-4" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.18)' }} data-testid={`my-order-${o.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-display" style={{ fontSize: '16px', fontWeight: 700 }}>Pedido #{o.id.slice(0, 8).toUpperCase()}</p>
                <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
                  {o.items.reduce((a, b) => a + b.qty, 0)} artículos · {o.address}
                </p>
                {o.delivery_date && (
                  <p className="font-body mt-1" style={{ fontSize: '11px', color: '#9A7522' }}>
                    Entrega: {new Date(o.delivery_date + 'T12:00:00').toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                )}
                <div className="font-body mt-2" style={{ fontSize: '11px', color: '#C2C1B6' }}>
                  <span>Subtotal: ${(o.subtotal || o.total).toFixed(2)}</span>
                  {o.tax_total > 0 && <span className="ml-3">IVU: ${o.tax_total.toFixed(2)}</span>}
                  {o.tip > 0 && <span className="ml-3">Propina: ${o.tip.toFixed(2)}</span>}
                  <span className="ml-3" style={{ color: '#C9A84C', fontWeight: 600 }}>Total: ${o.total.toFixed(2)}</span>
                </div>
              </div>
              <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '1px', color: '#C9A84C' }}>{STATUS_LABELS[o.status] || o.status}</span>
            </div>
          </Link>
        ))}
      </div>
    </PageShell>
  );
};

export default MyOrders;
